<template>
  <div class="so-copy" :class="{ 'so-copy--inline': inline }">
    <div ref="html-to-copy">
      <slot />
    </div>
    <div v-bind="$attrs" class="so-copy-btn">
      <SoTooltip
        :text="isCopied ? 'Copied' : 'Copy to Clipboard'"
        :disabled="isDisabled"
      >
        <SoButton
          v-if="!$slots.button"
          :disabled="isDisabled"
          :class="{ 'so-copy-btn--not-allowed': !elementToCopy }"
          type="icon"
          icon="copy"
          data-testid="so-copy-btn"
          @click="copyToClipboard()"
        />
        <!-- custom button slot -->
        <slot v-else name="button" :copy-to-clipboard="copyToClipboard" />
      </SoTooltip>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoButton from "./formControls/SoButton.vue"
  import SoTooltip from "./SoTooltip.vue"
  import errorReporter from "@/services/ErrorService/errorReporter"

  export type CopyToClipboardElement = HTMLElement | null

  export default defineComponent({
    components: { SoButton, SoTooltip },
    inheritAttrs: false,
    props: {
      elementToCopy: {
        type: HTMLElement as PropType<CopyToClipboardElement>,
        default: null,
      },
      plainText: {
        type: String,
        default: null,
      },
      inline: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        isCopied: false,
      }
    },
    computed: {
      isDisabled() {
        return !this.elementToCopy && !this.plainText && !this.$slots.default
      },
    },
    watch: {
      isCopied(value) {
        if (value) {
          setTimeout(() => {
            this.isCopied = false
          }, 1000)
        }
      },
    },
    methods: {
      selectElement(el: HTMLElement) {
        if (document.createRange) {
          const range = document.createRange()
          range.selectNodeContents(el)

          const selection = window.getSelection()
          selection?.removeAllRanges()
          selection?.addRange(range)
        }
      },
      formatTables(doc: Document) {
        const tableElements = doc.querySelectorAll("table, tr, td, th")
        if (tableElements.length) {
          tableElements.forEach((table) => {
            const htmlElement = table as HTMLElement
            htmlElement.style.border = "1px solid black"
            htmlElement.style.borderCollapse = "collapse"
            htmlElement.style.padding = "4px"
            htmlElement.style.marginBottom = "4px"
          })
        }
      },
      formatText(doc: Document) {
        const h5Elements = doc.querySelectorAll("h5")
        if (h5Elements.length) {
          h5Elements.forEach((h5Element) => (h5Element.style.fontSize = "28px"))
        }
        const mediumFonts = doc.querySelectorAll(
          "p.font-weight-medium, div.monthly-cost",
        )
        if (mediumFonts.length) {
          mediumFonts.forEach(
            (mediumFont) =>
              ((mediumFont as HTMLElement).style.fontWeight = "bold"),
          )
        }
      },
      formatMisc(doc: Document) {
        const expandedDetails = doc.querySelectorAll("div.expanded-details")
        if (expandedDetails.length) {
          expandedDetails.forEach((expandedDetail) => {
            const expandedDetailHTML = expandedDetail as HTMLElement
            expandedDetailHTML.style.display = "block"
            expandedDetailHTML.style.marginBottom = "20px"
          })
        }
        const includedCheckboxes = doc.querySelectorAll(
          ".included-payments-checkbox",
        )
        if (includedCheckboxes.length) {
          includedCheckboxes.forEach(
            (includedCheckbox) =>
              ((includedCheckbox as HTMLElement).style.display = "none"),
          )
        }
      },
      async copyToClipboard() {
        try {
          if (this.$slots.default) {
            const elementToCopy = this.$refs["html-to-copy"] as HTMLElement
            this.selectElement(elementToCopy)
            const itemToCopy = elementToCopy.outerHTML
            const blobInput = new Blob([itemToCopy], { type: "text/html" })
            const clipboardItemInput = new ClipboardItem({
              "text/html": blobInput,
            })
            navigator.clipboard
              .write([clipboardItemInput])
              .then(() => (this.isCopied = true))
          } else if (this.plainText) {
            navigator.clipboard
              .writeText(this.plainText)
              .then(() => (this.isCopied = true))
          } else {
            if (!this.elementToCopy) return
            this.selectElement(this.elementToCopy)
            const itemToCopy = this.elementToCopy.outerHTML
            const parser = new DOMParser()
            const doc = parser.parseFromString(itemToCopy, "text/html")

            this.formatTables(doc)
            this.formatText(doc)
            this.formatMisc(doc)

            const modifiedContent = new XMLSerializer().serializeToString(doc)
            const formattedPlainText = `<div style="text-align: left">${modifiedContent}</div>`

            const blobInput = new Blob([formattedPlainText], {
              type: "text/html",
            })
            const clipboardItemInput = new ClipboardItem({
              "text/html": blobInput,
            })
            navigator.clipboard
              .write([clipboardItemInput])
              .then(() => (this.isCopied = true))
          }
        } catch (err) {
          if (err instanceof Error) {
            console.error(err.name, err.message)
            errorReporter.report(err)
          }
        }
      },
    },
  })
</script>

<style scoped>
  :deep(.v-snackbar__content) {
    padding: 0;
  }

  .so-copy-btn {
    display: inline-block;
  }

  .so-copy-btn--not-allowed {
    cursor: not-allowed;
  }

  .so-copy--inline {
    display: inline-block;
  }
</style>
